import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Building2, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Magic-Link Einlösen-Page (/invite/:token).
 * Beim Mount wird automatisch /api/auth/invite-redeem aufgerufen.
 * Erfolg → Token gespeichert, Redirect je nach requires_profile.
 */
export function InviteRedeem() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setEmail, refreshMe } = useAuth();

  const [status, setStatus] = useState("loading"); // loading | success | error
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setStatus("error");
        setError("Kein Einladungs-Token in der URL.");
        return;
      }
      try {
        const res = await apiClient.post("/auth/invite-redeem", { token });
        if (cancelled) return;
        const { email, requires_profile, company_name } = res.data;
        localStorage.removeItem("authToken");
        localStorage.setItem("authEmail", email);
        setEmail(email);
        setData({ email, company_name, requires_profile });
        setStatus("success");
        await refreshMe();
        // Anzahl Companies prüfen → wenn ≥2, lande im Cockpit (Treuhänder-Modus).
        // Lightweight: /my/companies statt overview (kein KPI-Aggregations-Overhead).
        let multiCompany = false;
        try {
          const list = await apiClient.get("/my/companies");
          multiCompany = (list.data || []).length >= 2;
        } catch {
          // ignore
        }
        // Kurze Erfolgsmeldung, dann redirect
        setTimeout(() => {
          if (requires_profile) {
            navigate("/login", { replace: true }); // Profil-Step
          } else if (multiCompany) {
            navigate("/cockpit", { replace: true });
          } else {
            navigate("/", { replace: true });
          }
        }, 1200);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(e.response?.data?.detail || "Einladung konnte nicht eingelöst werden.");
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token, navigate, setEmail, refreshMe]);

  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center p-6"
      data-testid="invite-redeem-page"
    >
      <Card className="w-full max-w-md p-8 border border-slate-200 shadow-sm bg-white text-center">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="font-semibold text-slate-900">CH-Buchhaltung</div>
        </div>

        {status === "loading" && (
          <div className="space-y-3" data-testid="invite-loading">
            <Loader2 className="h-10 w-10 mx-auto text-slate-400 animate-spin" />
            <h1 className="text-xl font-semibold text-slate-900">
              Einladung wird geprüft …
            </h1>
            <p className="text-sm text-slate-500">
              Einen Moment bitte, wir bereiten Ihren Zugang vor.
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-3" data-testid="invite-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600" />
            <h1 className="text-xl font-semibold text-slate-900">Willkommen!</h1>
            {data?.company_name && (
              <p className="text-sm text-slate-600">
                Sie haben jetzt Zugriff auf <strong>{data.company_name}</strong>.
              </p>
            )}
            <p className="text-xs text-slate-400">
              {data?.requires_profile
                ? "Sie werden zu Ihrer Profil-Vervollständigung weitergeleitet …"
                : "Sie werden zum Dashboard weitergeleitet …"}
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4" data-testid="invite-error">
            <AlertTriangle className="h-12 w-12 mx-auto text-rose-600" />
            <h1 className="text-xl font-semibold text-slate-900">
              Einladung ungültig
            </h1>
            <p className="text-sm text-rose-700">{error}</p>
            <Link to="/login" className="inline-block">
              <Button
                type="button"
                className="rounded-full bg-slate-900 text-slate-50 px-6"
                data-testid="invite-error-login-button"
              >
                Zur normalen Anmeldung
              </Button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
